import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEO from '@/components/feature/SEO';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SchemaOrg from '@/components/feature/SchemaOrg';

const timeline = [
  { year: '2021', title: '初心萌芽', description: '创始人李明远在经历了一段消耗型关系后，深刻意识到"择善而交"的重要性，开始系统研究人际关系学和善意社交理念。' },
  { year: '2022', title: '理念成型', description: '经过一年多的研究和实践，"人善交"的核心理念体系初步形成。通过线下读书会和分享会的形式，开始小范围传播这一理念。' },
  { year: '2023', title: '社群建立', description: '人善交线上社群正式成立，吸引了一批志同道合的朋友加入。社群以"真诚、友善、成长"为核心价值观，成员之间形成了良好的互助氛围。' },
  { year: '2024', title: '内容深耕', description: '开始系统性地创作关于人际关系、交友智慧、情感成长的深度内容。文章在多平台获得广泛传播，累计阅读量突破百万。' },
  { year: '2025', title: '品牌升级', description: '人善交品牌全面升级，理念从"择善而交"拓展为"结交善良之人，共建美好人际圈"。团队不断壮大，影响力持续增长。' },
  { year: '2026', title: '全新启程', description: '人善交官方网站正式上线，致力于为更多人提供关于高质量人际关系的深度内容。我们相信，当每个人都学会择善而交时，世界会变得更加美好。' },
];

const reviews = [
  {
    name: '张女士',
    role: '企业管理者',
    content: '人善交的理念让我重新审视了自己的人际圈。按照文章中的方法清理了一些消耗型关系后，生活真的轻松了很多。现在身边都是一些真诚善良的朋友，每天都过得很充实。',
    rating: 5,
  },
  {
    name: '王先生',
    role: '自由职业者',
    content: '作为一个经常需要社交的自由职业者，人善交的文章给了我很多启发。特别是关于"善意社交的黄金法则"那篇，让我在社交中更加从容和自信。',
    rating: 5,
  },
  {
    name: '李同学',
    role: '在校大学生',
    content: '作为大学生，社交圈的建立对我来说非常重要。人善交的内容帮我避免了很多社交陷阱，也让我学会了如何辨别真正值得深交的朋友。非常感谢这个平台！',
    rating: 5,
  },
  {
    name: '陈女士',
    role: '心理咨询师',
    content: '从专业角度来看，人善交所倡导的理念与积极心理学高度契合。选择与善良之人交往，确实能够显著提升个人的幸福感和心理健康水平。我已经向很多来访者推荐了这个平台。',
    rating: 5,
  },
];

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);
    const websiteAlt = formData.get('website_alt') as string;

    if (websiteAlt && websiteAlt.trim() !== '') {
      setFormStatus('success');
      form.reset();
      setTimeout(() => setFormStatus('idle'), 3000);
      return;
    }

    try {
      const response = await fetch('https://readdy.ai/api/form/d9srcallbrt1qcgaj8o0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });

      const responseText = await response.text();
      let parsed: Record<string, unknown> | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        // not json
      }

      if (response.ok && parsed && (parsed as Record<string, unknown>).code === 'OK') {
        setFormStatus('success');
        form.reset();
        setTimeout(() => setFormStatus('idle'), 3000);
      } else {
        const serverMsg =
          (parsed && (parsed as Record<string, unknown>).meta && ((parsed as Record<string, unknown>).meta as Record<string, unknown>).message as string) ||
          (parsed && (parsed as Record<string, unknown>).message as string) ||
          responseText ||
          '提交失败，请稍后重试';
        if (typeof serverMsg === 'string' && (serverMsg.includes('spam') || serverMsg.includes('form data is spam'))) {
          setFormError('提交失败，请稍后重试');
        } else {
          setFormError(typeof serverMsg === 'string' ? serverMsg : '提交失败，请稍后重试');
        }
        setFormStatus('error');
      }
    } catch {
      setFormError('网络错误，请检查网络连接后重试');
      setFormStatus('error');
    }
  };

  return (
    <>
      <SEO
        title="关于我们 - 品牌介绍与联系方式"
        description="了解人善交的品牌故事与发展历程，查看用户口碑与评价。人善交致力于传播善意社交理念，帮助每个人建立高质量人际关系，远离消耗型关系。"
        keywords="人善交,关于我们,品牌介绍,用户口碑,联系方式"
        canonicalUrl="/about"
      />
      <SchemaOrg type="Organization" />

      <Navbar />
      <main className="pt-20">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="py-4">
            <Breadcrumb items={[{ label: '首页', path: '/' }, { label: '关于我们' }]} />
          </div>

          {/* Brand Intro */}
          <section className="py-10 md:py-16">
            <div className="max-w-3xl">
              <h1 className="text-3xl md:text-4xl font-bold text-foreground-900 mb-4"
                style={{ fontFamily: 'var(--font-heading)' }}>
                关于人善交
              </h1>
              <p className="text-lg text-foreground-600 leading-relaxed mb-6">
                人善交是一个专注于<strong>善意社交与人际关系成长</strong>的内容平台。
                我们相信，每个人都值得拥有真诚而温暖的人际关系，而这需要智慧和勇气——
                学会辨别善意，主动选择与善良之人同行。
              </p>
              <p className="text-foreground-600 leading-relaxed">
                在这个信息爆炸、社交泛滥的时代，我们将中国传统交友智慧与现代心理学研究成果相结合，
                为你提供关于<strong>高质量人际关系</strong>的深度内容。从人际关系的底层逻辑到实用的交友技巧，
                从辨别善意的方法到远离消耗型关系的策略——我们希望成为你人际关系成长路上的忠实伙伴。
              </p>
            </div>
          </section>

          {/* Brand Story Timeline */}
          <section className="py-10 md:py-16 bg-background-100 -mx-4 md:-mx-6 px-4 md:px-6">
            <div className="max-w-3xl mx-auto">
              <h2 className="text-2xl md:text-3xl font-bold text-foreground-900 text-center mb-10"
                style={{ fontFamily: 'var(--font-heading)' }}>
                发展历程
              </h2>
              <div className="relative">
                <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-px bg-accent-200 transform md:-translate-x-px"></div>
                {timeline.map((item, index) => (
                  <div
                    key={item.year}
                    className={`relative flex items-start gap-6 mb-8 ${
                      index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'
                    }`}
                  >
                    <div className="hidden md:flex flex-1"></div>
                    <div className="relative z-10 w-8 h-8 flex items-center justify-center rounded-full bg-accent-500 text-white flex-shrink-0">
                      <span className="text-xs font-bold">{index + 1}</span>
                    </div>
                    <div className="flex-1 bg-background-50 border border-background-200/60 rounded-xl p-4 md:p-5">
                      <span className="inline-block text-xs font-bold text-accent-600 bg-accent-100 px-2 py-0.5 rounded-full mb-2">
                        {item.year}
                      </span>
                      <h3 className="text-base font-semibold text-foreground-900 mb-1">{item.title}</h3>
                      <p className="text-sm text-foreground-600 leading-relaxed">{item.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Core Values */}
          <section className="py-10 md:py-16">
            <div className="max-w-4xl mx-auto text-center">
              <h2 className="text-2xl md:text-3xl font-bold text-foreground-900 mb-10"
                style={{ fontFamily: 'var(--font-heading)' }}>
                核心价值观
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
                <div className="p-6 bg-background-50 border border-background-200/60 rounded-xl">
                  <div className="w-12 h-12 flex items-center justify-center rounded-full bg-accent-100 text-accent-600 mx-auto mb-4">
                    <i className="ri-heart-line text-xl"></i>
                  </div>
                  <h3 className="text-base font-semibold text-foreground-900 mb-2">真诚</h3>
                  <p className="text-sm text-foreground-600 leading-relaxed">
                    真诚是所有人际关系的基石。我们倡导以真心换真心，不伪装、不虚伪。
                  </p>
                </div>
                <div className="p-6 bg-background-50 border border-background-200/60 rounded-xl">
                  <div className="w-12 h-12 flex items-center justify-center rounded-full bg-accent-100 text-accent-600 mx-auto mb-4">
                    <i className="ri-shield-star-line text-xl"></i>
                  </div>
                  <h3 className="text-base font-semibold text-foreground-900 mb-2">善意</h3>
                  <p className="text-sm text-foreground-600 leading-relaxed">
                    善意是社交的底色。我们相信善意的力量，它能让每一次交往都成为双向滋养。
                  </p>
                </div>
                <div className="p-6 bg-background-50 border border-background-200/60 rounded-xl">
                  <div className="w-12 h-12 flex items-center justify-center rounded-full bg-accent-100 text-accent-600 mx-auto mb-4">
                    <i className="ri-seedling-line text-xl"></i>
                  </div>
                  <h3 className="text-base font-semibold text-foreground-900 mb-2">成长</h3>
                  <p className="text-sm text-foreground-600 leading-relaxed">
                    良好的人际关系促进共同成长。我们希望通过优质的连接，让每个人都变得更好。
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* Reviews */}
          <section className="py-10 md:py-16 bg-background-100 -mx-4 md:-mx-6 px-4 md:px-6">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-2xl md:text-3xl font-bold text-foreground-900 text-center mb-10"
                style={{ fontFamily: 'var(--font-heading)' }}>
                用户口碑
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                {reviews.map((review, index) => (
                  <div
                    key={index}
                    className="bg-background-50 border border-background-200/60 rounded-xl p-5"
                  >
                    <div className="flex items-center gap-1 mb-3">
                      {Array.from({ length: review.rating }).map((_, i) => (
                        <i key={i} className="ri-star-fill text-amber-400 text-sm"></i>
                      ))}
                    </div>
                    <p className="text-sm text-foreground-700 leading-relaxed mb-4">
                      "{review.content}"
                    </p>
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 flex items-center justify-center rounded-full bg-accent-100 text-accent-600 text-xs font-bold">
                        {review.name.charAt(0)}
                      </div>
                      <div>
                        <p className="text-xs font-semibold text-foreground-900">{review.name}</p>
                        <p className="text-xs text-foreground-500">{review.role}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Contact */}
          <section className="py-10 md:py-16">
            <div className="max-w-2xl mx-auto">
              <h2 className="text-2xl md:text-3xl font-bold text-foreground-900 text-center mb-4"
                style={{ fontFamily: 'var(--font-heading)' }}>
                联系我们
              </h2>
              <p className="text-sm text-foreground-600 text-center mb-8">
                有任何问题、建议或合作意向，欢迎通过以下表单与我们联系
              </p>

              <form
                onSubmit={handleSubmit}
                data-readdy-form="true"
                className="bg-background-50 border border-background-200/60 rounded-xl p-6 md:p-8 space-y-4"
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      姓名 <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      required
                      className="w-full px-4 py-2.5 text-sm border border-background-300/60 rounded-lg bg-white focus:outline-none focus:border-accent-400 transition-colors"
                      placeholder="请输入您的姓名"
                    />
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      邮箱 <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      required
                      className="w-full px-4 py-2.5 text-sm border border-background-300/60 rounded-lg bg-white focus:outline-none focus:border-accent-400 transition-colors"
                      placeholder="请输入您的邮箱"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="subject" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    主题 <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="subject"
                    name="subject"
                    required
                    className="w-full px-4 py-2.5 text-sm border border-background-300/60 rounded-lg bg-white focus:outline-none focus:border-accent-400 transition-colors"
                    placeholder="请输入主题"
                  />
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    留言 <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    required
                    maxLength={500}
                    rows={5}
                    className="w-full px-4 py-2.5 text-sm border border-background-300/60 rounded-lg bg-white focus:outline-none focus:border-accent-400 transition-colors resize-none"
                    placeholder="请输入您的留言（最多500字）"
                  ></textarea>
                </div>

                <input
                  type="text"
                  name="website_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  readOnly
                  aria-hidden="true"
                  className="form-honeypot"
                />

                <button
                  type="submit"
                  className="w-full py-3 bg-accent-500 text-white rounded-lg text-sm font-medium hover:bg-accent-600 cursor-pointer transition-all duration-300 whitespace-nowrap"
                >
                  提交留言
                </button>

                {formStatus === 'success' && (
                  <p className="text-sm text-green-600 text-center">
                    感谢您的留言！我们会尽快与您联系。
                  </p>
                )}
                {formStatus === 'error' && (
                  <p className="text-sm text-red-500 text-center">
                    {formError || '提交失败，请稍后重试'}
                  </p>
                )}
              </form>
            </div>
          </section>
        </div>
      </main>
      <Footer />
    </>
  );
}